import Link from 'next/link'
import * as R from 'ramda'
import React, { useContext, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import styled, { ThemeContext } from 'styled-components'

import { Col, Row } from '../../../components/Layout/FlexGrid'
import MaxWidth from '../../../components/Layout/MaxWidth'
import NextSeo from '../../../components/NextSeo'
import ProjectAds from '../../../components/ProjectAds'
import { ProjectDetailSkeleton } from '../../../components/Skeleton/index'
import SvgIcon from '../../../components/SvgIcon'
import SectionTitle from '../../../components/Text/SectionTitle'
import { PROJECT_STATUS } from '../../../constants'
import { PAGE_VIEW } from '../../../constants/dataTracking'
import { DonateNow } from '../../../containers/Appview'
import DonationListDrawer from '../../../containers/Appview/DonationListDrawer'
import Button from '../../../containers/Button'
import DonationStickySidebar from '../../../containers/DonationStickySidebar'
import OrganizationInfo from '../../../containers/OrganizationDetail/OrganizationInfo'
import PersonalFrCtaCard from '../../../containers/PersonalFundraising/CtaCard'
import DonorList from '../../../containers/PersonalFundraising/DonorList'
import Ranking from '../../../containers/PersonalFundraising/Ranking'
import ProjectCard from '../../../containers/ProjectCard'
import DonationList from '../../../containers/ProjectDetail/DonationList'
import FeaturedProject from '../../../containers/ProjectDetail/FeaturedProject'
import ProjectContent from '../../../containers/ProjectDetail/ProjectContent'
import { useMq, useTracking } from '../../../hooks'
import { useTranslation, withTranslation } from '../../../i18n'
import CreateFundraisingEventBtnSvg from '../../../public/static/images/icon-fundraising-create-button.svg'
import { generateShareMsg } from '../../../redux/app/actions'
import {
  getDonationStatsRequest,
  getProjectDetailPageRequest
} from '../../../redux/project/actions'
import {
  getProjectDetailHighlightedProjectsNos,
  orgSelector,
  projectSelector
} from '../../../redux/project/selectors'
import { fireGtmEvent } from '../../../redux/tracking/actions'
import { getColor } from '../../../utils/cssUtils'
import {
  detailClickCreateFundraisingEventBtn,
  detailClickOrganization,
  detailCloseDonatePopup,
  detailReachOrganization,
  //
  detailView
} from '../../../utils/dataTracker'
import mq from '../../../utils/mediaQuery'

const Wrapper = styled.div`
  width: 100%;
  background-color: #fff;
`
const Hero = styled(MaxWidth)`
  padding: 56px 0px 40px 0px;
  ${mq.mobile} {
    padding: 24px 0px 16px 0px;
  }
`
const Main = styled.div`
  width: 100%;
  margin: 0 -12px;
  // clearfix
  &:before,
  &:after {
    content: ' ';
    display: table;
  }
  &:after {
    clear: both;
  }
  ${mq.tabletMobile} {
    display: flex;
    flex-flow: column;
    margin: 0;
  }
`
const ContentWrapper = styled.div`
  width: 66.66666%;
  padding: 0 12px;
  float: left;
  ${mq.tabletMobile} {
    width: 100%;
    padding: 0;
    order: 2;
  }
`
const SideBarTop = styled.div`
  width: 33.33333%;
  padding: 0 12px;
  float: left;
  margin-bottom: 16px;
  ${mq.tabletMobile} {
    order: 0;
    width: 100%;
    padding: 0;
  }
`
const Highlighted = styled.div`
  padding: 32px 0px 56px 0px;
  background: ${getColor('lightYellow')};
  box-shadow: inset 0 2px 23px 0 #e4e6e9;
`
const Divider = styled.div`
  margin: 40px 0;
  height: 2px;
  width: 100%;
  border: solid 1px ${getColor('border')};
`
const StyledSvgIcon = styled(SvgIcon)`
  margin-right: 4px;
  width: 20px;
`

const PersonalFundraisingCta = ({ projectNo, projectName }) => {
  const { t } = useTranslation()

  const track = useTracking()

  const { color } = useContext(ThemeContext)

  const handleClick = () => {
    track(detailClickCreateFundraisingEventBtn(projectNo, projectName))
  }

  return (
    <PersonalFrCtaCard
      button={
        <Link href={`/fundraising/create/${projectNo}`}>
          <a>
            <Button
              type='button'
              bgColor={color.lightYellow}
              textColor={color.theme}
              height={48}
              onClick={handleClick}
            >
              <StyledSvgIcon svg={<CreateFundraisingEventBtnSvg />} />
              {t('personalFundraising.ctaButton.createNow')}
            </Button>
          </a>
        </Link>
      }
    />
  )
}

const MainContent = React.memo(
  ({
    isAppview,
    projectNo,
    projectName,
    isFundraisingEligible,
    supportsFundraisingEvent
  }) => {
    const { isTabletMobile, isDesktop } = useMq()

    if (R.isNil(isTabletMobile)) return null

    return (
      <>
        <ContentWrapper xl={16} md={24}>
          <ProjectContent projectNo={projectNo} />
          {(isAppview || isTabletMobile) && <DonorList projectNo={projectNo} />}
          {isTabletMobile && supportsFundraisingEvent && (
            <Ranking projectNo={projectNo} />
          )}
        </ContentWrapper>
        {isFundraisingEligible && (
          <SideBarTop>
            <PersonalFundraisingCta
              projectNo={projectNo}
              projectName={projectName}
            />
          </SideBarTop>
        )}
        {!isAppview && (
          <DonationStickySidebar offsetTop={64 + 16}>
            <DonationList projectNo={projectNo} />
            <ProjectAds />
            {!isTabletMobile && supportsFundraisingEvent && (
              <Ranking projectNo={projectNo} />
            )}
            {isDesktop && <DonorList projectNo={projectNo} />}
          </DonationStickySidebar>
        )}
      </>
    )
  }
)

const ProjectPage = ({ t, projectNo }) => {
  const track = useTracking()

  const dispatch = useDispatch()

  const isInitialRender = useSelector(R.path(['appState', 'isInitialRender']))

  const isAppview = useSelector(R.path(['appState', 'isAppview']))

  const fetching = useSelector(
    R.path(['projectState', 'fetching', 'getProjectDetailPage'])
  )

  const highlightedProjectsNos = useSelector(
    getProjectDetailHighlightedProjectsNos,
    R.equals
  )

  const projectData = useSelector(
    state => projectSelector(state, projectNo),
    R.equals
  )

  const organizationData = useSelector(
    state => orgSelector(state, projectData?.organizationNo),
    R.equals
  )

  const handleCloseClickTracking = () => {
    const { name } = projectData

    detailCloseDonatePopup(projectNo, name)
  }

  const renderOrganizationInfo = () => {
    const { name } = projectData

    const { organizationNo } = organizationData

    return (
      <>
        <OrganizationInfo
          showProjectsCount
          projectNo={projectNo}
          organizationNo={organizationNo}
          clickOrgTracking={detailClickOrganization}
        />
        <Waypoint
          onEnter={() => {
            detailReachOrganization(projectNo, name)
          }}
        />
      </>
    )
  }

  const isFundraisingEligible = () => {
    const { projectStatus, supportsFundraisingEvent } = projectData ?? {}

    return (
      projectStatus === PROJECT_STATUS.IN_PROGRESS && supportsFundraisingEvent
    )
  }

  const handleProjectReady = () => {
    const projectName = R.pathOr('', ['name'], projectData)

    track(detailView(projectNo, projectName, isFundraisingEligible()), true)

    dispatch(
      fireGtmEvent(PAGE_VIEW, {
        ContentType: 'product',
        Content: projectName,
        ContentID: projectNo
      })
    )

    dispatch(
      generateShareMsg(
        t('shareMsg.project', {
          orgName: R.pathOr('', ['name'], organizationData),
          projectName
        })
      )
    )
  }

  useEffect(() => {
    if (isInitialRender) {
      dispatch(
        getDonationStatsRequest(
          R.concat(highlightedProjectsNos, R.append(projectNo, []))
        )
      )
    }

    handleProjectReady()
  }, [])

  useEffect(() => {
    handleProjectReady()
  }, [projectNo])

  if (R.isNil(projectData)) return null

  return (
    <Wrapper>
      <NextSeo
        image={R.pathOr(
          R.pathOr(
            process.env.ASSET_PREFIX + process.env.OG_IMG_PATH,
            ['mainImg'],
            projectData
          ),
          ['ogImage'],
          projectData
        )}
        title={R.pathOr(
          R.always(
            t('seo.detailPage.ogTitle', {
              projectName: R.pathOr('', ['name'], projectData),
              orgName: R.pathOr('', ['name'], organizationData)
            })
          )(),
          ['ogTitle'],
          projectData
        )}
        description={R.pathOr(
          R.always(
            t('seo.detailPage.ogDescription', {
              orgName: R.pathOr('', ['name'], organizationData)
            })
          )(),
          ['ogDescription'],
          projectData
        )}
      />
      {fetching ? (
        <ProjectDetailSkeleton />
      ) : (
        <>
          <Hero>
            <FeaturedProject projectNo={projectNo} />
          </Hero>
          <MaxWidth>
            <Main>
              <MainContent
                isAppview={isAppview}
                projectNo={projectNo}
                projectName={projectData?.name}
                isFundraisingEligible={isFundraisingEligible()}
                supportsFundraisingEvent={projectData?.supportsFundraisingEvent}
              />
            </Main>
          </MaxWidth>
          <MaxWidth>
            <Divider />
          </MaxWidth>
          {renderOrganizationInfo()}
          {!R.isEmpty(highlightedProjectsNos) && (
            <Highlighted>
              <MaxWidth>
                <SectionTitle>{t('highlightedProject.title')}</SectionTitle>
                <Row
                  gutter={{ xl: 24, md: 24, xs: 16 }}
                  css={`
                    margin-bottom: 24px;
                  `}
                >
                  {R.addIndex(R.map)(
                    (projectNo, i) =>
                      i <= 5 && (
                        <Col xs={24} md={12} xl={8} key={projectNo}>
                          <ProjectCard
                            projectNo={projectNo}
                            index={i}
                            projectType='featured'
                          />
                        </Col>
                      ),
                    highlightedProjectsNos
                  )}
                </Row>
              </MaxWidth>
            </Highlighted>
          )}
          {isAppview && (
            <DonationListDrawer onCloseClickTracking={handleCloseClickTracking}>
              <DonationList projectNo={projectNo} />
            </DonationListDrawer>
          )}
        </>
      )}
    </Wrapper>
  )
}

const Project = withTranslation('common')(ProjectPage)

export default Project

Project.renderPersistentLayout = () => {
  return {
    appviewNavigation: () => <DonateNow />
  }
}

Project.getInitialProps = async ctx => {
  const { query, store } = ctx

  const { projectNo, projectName, previewToken } = query

  if (projectNo) {
    store.dispatch(
      getProjectDetailPageRequest(
        encodeURIComponent(projectNo),
        ctx,
        previewToken
      )
    )
  }

  return {
    projectNo,
    projectName
  }
}
